import { datadogRum } from "@datadog/browser-rum";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { EHR_SYNC_OUTBOUND_GATED_MESSAGE, decideSyncToEhrAction } from "@utils/ehrActions";
import { getPayorConfig } from "@utils/payorConfig";
import { formatDate } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
  Badge,
  Button,
  Paragraph,
  SelectItem,
  Tooltip,
  TrainStopProgressNav,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiPayorReportSubmissionStatusChoices,
  ApiReportEhrSyncChoices,
  ApiReportReportStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
  EhrConnectionStatus,
  ExportTypeEnum,
  ReportStatusEnums,
  ReportType,
  useEhrGetClientListMutation,
  useStartGoalsSyncToEhrMutation,
  useUpdateReportMutation,
} from "@api/graphql/types-and-hooks";
import { StatusType } from "@components/AssessmentReportsTable/AssessmentReportsTableV2";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { EHROrgContactDialog } from "@components/ClinicalNewReportDialog/EHROrgContactDialog";
import { EhrSyncFailureStrip } from "@components/EHRSyncAlert/EhrExportSyncFailureBanner";
import { Layout, LayoutSubheader } from "@components/Layout";
import ExportWithTemplateV2 from "@components/PDFGenerator/ExportWithTemplateV2";
import { ReviewDataProvider } from "@components/QaReview/components/ReviewDataProvider";
import { ReviewSessionProvider } from "@components/QaReview/components/ReviewSessionProvider";
import { ReviewViewSwitch } from "@components/QaReview/components/ReviewViewSwitch";
import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { PayorSubmissionDialog } from "@components/ReportCompletionAndExport/PayorSubmissionDialog";
import { PayorSubmittingDialog } from "@components/ReportCompletionAndExport/PayorSubmittingDialog";
import { EXPORT_OPTIONS, ReportExportDialog } from "@components/ReportCompletionAndExport/ReportExportDialog";
import { FinishReviewProvider } from "@components/ReportReview/FinishReviewProvider";
import { ReportReviewControls } from "@components/ReportReview/ReportReviewControls";
import { ReviewerBanner } from "@components/ReportReview/ReviewerBanner";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { SyncConfirmDialog } from "@components/SyncConfirmDialog/SyncConfirmDialog";
import { WizardStepTracker } from "@components/analytics/WizardStepTracker";
import { convertReadableString } from "@components/forms/utils";
import {
  notifyError,
  notifyErrorPersistent,
  notifySuccess,
  notifySuccessPersistent,
} from "@components/notifications/notifications";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { usePayorReportSubmission } from "@hooks/usePayorReportSubmission";
import { useTrackReportOpened } from "@hooks/useReportAnalytics";
import { useReportExport } from "@hooks/useReportExport";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { getFilteredPageSectionsByLocation } from "@pages/AssessmentReportV2Details/Components/helper";
import { MissingReportPage } from "@pages/AssessmentReportV2Details/MissingReportPage";
import { getOrderedAssessmentReportSubRoutes, routeLabelMapping } from "@pages/AssessmentReportV2Details/helpers";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { isGeneratingSection } from "@pages/EvaluationDetails/Components/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { ReportNode, useReport } from "@providers/ReportProvider";
import { SidebarViewProvider } from "@providers/SidebarViewProvider/SidebarViewProvider";
import { getStatusAndRedirectRouteFromReport } from "@routes/AssessmentReportsV2/AssessmentReportsHelper";

const IA_ASSESSMENT_REPORTS_V2_STEP_ORDER = [
  "client-details",
  "upload-files",
  "report-part-1",
  "prioritize",
  "short-term-goals",
  "report-part-2",
  "review-and-sign",
];

interface ButtonConfig {
  key: string;
  condition: boolean;
  text: string;
  onClick: () => void;
  disabled: boolean;
}

const AssessmentReportV2DetailsContent: React.FC = () => {
  const { pathname, state } = useLocation();
  const [searchParams] = useSearchParams();
  const forceEhrFailure = searchParams.get("forceEhrFailure") === "true";
  const flags = useFlags();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEHRLogin, setIsEHRLogin] = useState<boolean>(false);
  const [isEhrOrgContactDialogOpen, setIsEhrOrgContactDialogOpen] = useState<boolean>(false);
  const [isEHRLoginSuccess, setIsEHRLoginSuccess] = useState<boolean>(false);
  const [ehrLoginOutcome, setEhrLoginOutcome] = useState<"existing" | "fresh" | null>(null);
  const [pendingSyncAfterLogin, setPendingSyncAfterLogin] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_OPTIONS[0]);
  const [isPayorSubmittingDialogOpen, setIsPayorSubmittingDialogOpen] = useState<boolean>(false);
  const [isPayorSubmissionDialogOpen, setIsPayorSubmissionDialogOpen] = useState<boolean>(false);
  const [payorSubmissionFailed, setPayorSubmissionFailed] = useState<boolean>(false);
  const [payorSubmissionErrorMessage, setPayorSubmissionErrorMessage] = useState<string>("");
  const [isAwaitingSubmissionOutcome, setIsAwaitingSubmissionOutcome] = useState<boolean>(false);

  const { report, reportQuery } = useReport();
  const isReportQaScannerEnabled = useIsQaScannerEnabled();

  const reportIsCompleted = report.reportStatus === ApiReportReportStatusChoices.Completed;

  const { assessmentId } = useParams<{ assessmentId: string }>();
  const reportContext = {
    report_id: assessmentId ?? "",
    report_type: "initial_assessment" as const,
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  };
  useTrackReportOpened(reportContext);

  const refetchReportForPdf = useCallback(async (): Promise<ReportType | undefined> => {
    const result = await reportQuery.refetch();
    return result.data?.getReports?.edges?.[0]?.node as ReportType | undefined;
  }, [reportQuery]);

  const reportId = report?.id ?? null;

  const payorType = report.reportTemplate?.payorType;
  const payorCopy = getPayorConfig(payorType);
  const {
    isPayorWithSubmissionIntegration,
    isPayorSubmissionEnabled,
    isSubmittingToPayorInProgress,
    isLoadingPayorReportSubmissionStatus,
    startPayorSubmissionWorkflow,
    payorReportSubmissionStatus,
    refetchPayorReportSubmissionStatus,
  } = usePayorReportSubmission({
    reportId,
    isReportCompleted: reportIsCompleted,
    payorType,
  });

  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;
  const headerTitle = `${clientName.trim()} Assessment Report`;

  const { status: helperHeaderStatus } = getStatusAndRedirectRouteFromReport(report);

  // Overlay the header status with the live payor submission status from
  // usePayorReportSubmission. The helper reads from `report.payorreportsubmissionSet`
  // (assessmentQuery), which may lag the hook's submission-list query right
  // after a Submit. Whichever surface freshens first drives the badge.
  // The "Submitted" UI state spans the moment the backend creates a submission
  // (INITIALIZED) through to it reaching COMPLETED but no review yet. Used to
  // both drive the badge and hide Edit / Submit buttons in this window.
  const isInSubmittedState =
    isPayorWithSubmissionIntegration &&
    reportIsCompleted &&
    (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Initialized ||
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.InProgress ||
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Completed);

  // Suppress Edit/Submit while we're still resolving the payor submission
  // status on a completed payor-integrated report — otherwise the buttons
  // flash in briefly (status is null → not Submitted) and then disappear
  // once the query returns COMPLETED. Hiding during the load avoids the flicker.
  const isResolvingPayorStatus =
    isPayorWithSubmissionIntegration && reportIsCompleted && isLoadingPayorReportSubmissionStatus;

  const livePostCompletionStatus: StatusType | null = (() => {
    if (!isPayorWithSubmissionIntegration || !reportIsCompleted) return null;
    switch (payorReportSubmissionStatus) {
      case ApiPayorReportSubmissionStatusChoices.Approved:
        return "Approved";
      case ApiPayorReportSubmissionStatusChoices.NeedsRevision:
        return "Needs Revision";
      case ApiPayorReportSubmissionStatusChoices.Completed:
      case ApiPayorReportSubmissionStatusChoices.Initialized:
      case ApiPayorReportSubmissionStatusChoices.InProgress:
        return "Submitted";
      default:
        return null;
    }
  })();
  const headerStatus: StatusType = livePostCompletionStatus ?? helperHeaderStatus;

  const { handleExport, isExporting: isReportExporting } = useReportExport({
    report: report,
    exportType: selectedExportType.id as ExportTypeEnum,
    clientName,
    onExportComplete: () => setIsExportModalOpen(false),
  });

  const {
    generateAndUploadPdf,
    isExporting: isGeneratingPdfForSubmit,
    isExportDataReady,
  } = useTemplateExport({
    reportData: report,
    exportType: ExportTypeEnum.Pdf,
    refetchReport: refetchReportForPdf,
  });

  const {
    isPassageHealthUser,
    isTherapzUser,
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isHippHealthUser,
    isRethinkUser,
    isEnsoraUser,
    isFronteraEhrUser,
    isEhrSyncCleared,
  } = useAdminData();
  const ehrName = isFronteraEhrUser ? "Frontera EHR" : convertReadableString(report.organization?.organizationType);
  const effectiveEhrSync = forceEhrFailure ? ApiReportEhrSyncChoices.Failure : report?.ehrSync;
  const { isCRDataWarehouse, isOrgUsernamePassword, ehrConnectionStatus, hasCredentials } = useEHRConnection();

  // Track previous ehrSync status to detect changes
  const prevEhrSyncStatusRef = useRef<ApiReportEhrSyncChoices | null | undefined>(report?.ehrSync);
  const loginFailureHandledRef = useRef(false);

  const updateAssessment = useUpdateReportMutation({});
  const ehrGetClientList = useEhrGetClientListMutation();
  // Trigger client list fetch after successful EHR login
  useEffect(() => {
    if (!isEHRLoginSuccess) return;
    // User clicked sync before logging in — auto-sync now, skip confirm dialog
    if (pendingSyncAfterLogin) {
      setPendingSyncAfterLogin(false);
      setIsEHRLoginSuccess(false);
      setIsEHRLogin(false);
      setEhrLoginOutcome(null);
      syncGoalsToEhr();
      return;
    }
    // For existing session: open confirm immediately.
    if (ehrLoginOutcome === "existing") {
      setDialogOpen(true);
      setIsEHRLogin(false); // Close login dialog
      setEhrLoginOutcome(null);
      return;
    }
    // For fresh credential login: fetch client list then open confirm.
    if (ehrLoginOutcome === "fresh") {
      // CentralReach sync users manage imports separately via Client Dashboard
      if (isCentralReachUser) {
        setDialogOpen(true);
        setIsEHRLogin(false);
        setEhrLoginOutcome(null);
        return;
      }
      ehrGetClientList
        .mutateAsync({})
        .then(() => {
          setDialogOpen(true);
          setIsEHRLogin(false); // Close login dialog
        })
        .catch((error) => {
          datadogRum.addError(error, { context: "Error fetching EHR client list" });
          notifyError("Failed to fetch client list from EHR");
          setIsEHRLogin(false); // Close login dialog on error
        })
        .finally(() => {
          setEhrLoginOutcome(null);
        });
    }
  }, [isEHRLoginSuccess, ehrLoginOutcome, isCentralReachUser, pendingSyncAfterLogin]);

  // When a sync fails due to credential failure, auto-open the login dialog so
  // the user can re-enter credentials. After re-login the confirm dialog appears
  // (via the login-success effect) so the user still explicitly confirms the retry.
  useEffect(() => {
    if (
      report?.ehrSync === ApiReportEhrSyncChoices.Failure &&
      ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed &&
      !loginFailureHandledRef.current &&
      !isCRDataWarehouse &&
      !isOrgUsernamePassword
    ) {
      loginFailureHandledRef.current = true;
      setIsEHRLogin(true);
    }
    if (report?.ehrSync !== ApiReportEhrSyncChoices.Failure) {
      loginFailureHandledRef.current = false;
    }
  }, [report?.ehrSync, ehrConnectionStatus, isCRDataWarehouse, isOrgUsernamePassword]);

  const { orderedRoutes, statusFromCurrentRoute } = getOrderedAssessmentReportSubRoutes();

  const updateAssessmentStatus = async () => {
    await updateAssessment
      .mutateAsync({
        reportId: report.id,
        status: ReportStatusEnums.Draft,
      })
      .then((data) => {
        if (data.updateReport?.status) {
          reportQuery.refetch();
        }
      });
  };

  const runSubmitToPayor = async () => {
    setPayorSubmissionFailed(false);
    setPayorSubmissionErrorMessage("");
    setIsPayorSubmissionDialogOpen(false);
    setIsPayorSubmittingDialogOpen(true);

    // Under unified export, the TS-worker generation that ran at sign time
    // produced the canonical PDF in S3. Skip the frontend PDF upload so we
    // don't clobber that file — but only when the server PDF actually exists.
    // Falls back to frontend generation for reports signed before unified export
    // was enabled, an in-flight TS-worker (status Generating, URL not yet
    // populated), or a failed server-side export.
    const hasServerPdf = !!report?.reportPdfFileSignedUrl;
    const skipFrontendPdfGeneration = isDocxTemplaterExport && isUnifiedExport && hasServerPdf;
    let pdfFailed = false;
    if (!skipFrontendPdfGeneration) {
      try {
        await generateAndUploadPdf();
      } catch (error) {
        pdfFailed = true;
        console.error("[payor-submit] generateAndUploadPdf threw:", error);
        datadogRum.addError(error, { context: payorCopy?.errorContexts.pdfGenerationFailed });
      }
    }

    if (pdfFailed) {
      setIsPayorSubmittingDialogOpen(false);
      setPayorSubmissionFailed(true);
      setIsPayorSubmissionDialogOpen(true);
      return;
    }

    let workflowStarted = false;
    try {
      const data = await startPayorSubmissionWorkflow({ reportId });
      if (data?.startPayorSubmissionWorkflow?.status === false) {
        setPayorSubmissionErrorMessage(data?.startPayorSubmissionWorkflow?.message ?? "");
      } else if (data?.startPayorSubmissionWorkflow?.status) {
        await Promise.all([refetchPayorReportSubmissionStatus(), reportQuery.refetch()]);
        workflowStarted = true;
      }
    } catch (error: unknown) {
      console.error("[payor-submit] startPayorSubmissionWorkflow threw:", error);
      datadogRum.addError(error, { context: payorCopy?.errorContexts.submissionFailed });
      if (error instanceof Error && error.message) {
        setPayorSubmissionErrorMessage(error.message);
      }
    }

    if (!workflowStarted) {
      setIsPayorSubmittingDialogOpen(false);
      setPayorSubmissionFailed(true);
      setIsPayorSubmissionDialogOpen(true);
      return;
    }

    // Workflow accepted on the backend. The submitting dialog stays open while
    // the workflow runs (payor round-trip). The useEffect below watches
    // payorReportSubmissionStatus and resolves the loader on a terminal state:
    // Completed → success toast, Failed → reopen dialog with inline error,
    // Approved/NeedsRevision → close (helper takes over the badge).
    setIsAwaitingSubmissionOutcome(true);
  };

  // PROA-2622: the same double-click defect this ticket fixes for exports, on a
  // costlier path -- `generateAndUploadPdf` runs the same `buildAndUploadDocx`
  // that flips the report COMPLETED -> GENERATING, and a second click also
  // starts a second payor submission workflow. The Submit button's `isSubmitting`
  // is derived state, so it only disables a render too late.
  const handleSubmitToPayor = useInFlightOnce(runSubmitToPayor);

  // Resolves the submitting-loader once the workflow polling lands on a
  // terminal state. Bound to handleSubmitToPayor's setIsAwaitingSubmissionOutcome
  // — keeps the loader open through the full payor round-trip rather than
  // closing on the (much faster) startPayorSubmissionWorkflow mutation.
  useEffect(() => {
    if (!isAwaitingSubmissionOutcome) return;
    const status = payorReportSubmissionStatus;
    if (status === ApiPayorReportSubmissionStatusChoices.Failed) {
      setIsAwaitingSubmissionOutcome(false);
      setIsPayorSubmittingDialogOpen(false);
      setPayorSubmissionFailed(true);
      setIsPayorSubmissionDialogOpen(true);
      return;
    }
    if (
      status === ApiPayorReportSubmissionStatusChoices.Completed ||
      status === ApiPayorReportSubmissionStatusChoices.Approved ||
      status === ApiPayorReportSubmissionStatusChoices.NeedsRevision
    ) {
      setIsAwaitingSubmissionOutcome(false);
      setIsPayorSubmittingDialogOpen(false);
      if (status === ApiPayorReportSubmissionStatusChoices.Completed && payorCopy) {
        notifySuccess(payorCopy.notifications.submitted);
      }
      void reportQuery.refetch();
    }
  }, [isAwaitingSubmissionOutcome, payorReportSubmissionStatus, reportQuery, payorCopy]);

  const getPayorButtonTooltipText = (): string => {
    if (!payorCopy) return "disabled";
    if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Approved)
      return payorCopy.tooltips.approved;
    else if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Completed)
      return payorCopy.tooltips.awaitingReview;
    else if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision)
      return payorCopy.tooltips.needsRevision;
    else if (
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Initialized ||
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.InProgress
    )
      return payorCopy.tooltips.submitting;
    else return "disabled";
  };

  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);

  const { isPending: ehrSyncPending, mutateAsync: startEhrSync } = useStartGoalsSyncToEhrMutation({});
  const syncGoalsToEhr = async () => {
    // Managed-onboarding gate: an uncleared tenant can't sync (the backend
    // rejects it too). The button is disabled; guarded here as defense-in-depth.
    if (!isEhrSyncCleared) return;
    startEhrSync({ reportId: report.id })
      .then(async (data) => {
        if (data?.startGoalsSyncToEhr?.status) {
          notifySuccess(`Sync Started. This treatment plan has been sent to ${ehrName}`);
          await reportQuery.refetch();
        }
      })
      .catch((error) => {
        datadogRum.addError(error, { context: `Error Starting ${ehrName} Sync` });
        notifyError(`Error Starting ${ehrName} Sync`);
      });

    setDialogOpen(false);
  };

  const buttons: ButtonConfig[] = [
    {
      key: "sync-new-goals-to-ehr",
      condition:
        isPassageHealthUser ||
        isTherapzUser ||
        isHiRasmusUser ||
        isMotivityUser ||
        isEnsoraUser ||
        (isCentralReachUser && !isCRDataWarehouse) ||
        (isHippHealthUser && flags["hipphealth-sync"]) ||
        isRethinkUser ||
        isFronteraEhrUser,
      text: `Sync to ${ehrName}`,
      onClick: () => {
        // Managed-onboarding gate: block the first EHR touch for an uncleared
        // tenant. The button is disabled; guarded here as defense-in-depth.
        if (!isEhrSyncCleared) return;
        const action = decideSyncToEhrAction({
          // Frontera EHR exports through FDM's own vertical client, so it needs no EHR login.
          isApiKeyOrg: isTherapzUser || isPassageHealthUser || isHippHealthUser || isFronteraEhrUser,
          isOrgUsernamePassword,
          hasCredentials,
          ehrConnectionStatus,
        });
        if (action === "open-sync") {
          setDialogOpen(true);
        } else if (action === "open-org-contact") {
          // Org EHR genuinely not set up — the provider has no credentials of their
          // own, so direct them to their organization administrator.
          setIsEhrOrgContactDialogOpen(true);
        } else if (action === "open-login") {
          // Known invalid credentials — open login dialog directly; confirm dialog appears after re-login
          setIsEHRLoginSuccess(false);
          setEhrLoginOutcome(null);
          setIsEHRLogin(true);
        } else {
          // open-login-pending-sync: log in first, then sync once login succeeds.
          setIsEHRLoginSuccess(false);
          setEhrLoginOutcome(null);
          setPendingSyncAfterLogin(true);
          setIsEHRLogin(true);
        }
      },
      disabled:
        (!!report.ehrSync && report.ehrSync != ApiReportEhrSyncChoices.Failure) ||
        !report.learner?.externalId ||
        ehrSyncPending ||
        !isEhrSyncCleared,
    },
    {
      key: "submit-to-payor",
      // Hide entirely once the report has been Submitted (awaiting review) or
      // Approved. Resubmit surfaces again when the review comes back as
      // NeedsRevision.
      condition:
        isPayorSubmissionEnabled &&
        !isInSubmittedState &&
        !isResolvingPayorStatus &&
        payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Approved,
      text:
        payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision
          ? payorCopy?.button.resubmit ?? ""
          : payorCopy?.button.submit ?? "",
      onClick: () => {
        setPayorSubmissionFailed(false);
        setIsPayorSubmissionDialogOpen(true);
      },
      disabled:
        isLoadingPayorReportSubmissionStatus ||
        !!(
          payorReportSubmissionStatus &&
          payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Failed &&
          payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.NeedsRevision
        ) ||
        isSubmittingToPayorInProgress ||
        isGeneratingPdfForSubmit,
    },
  ];

  const getPassageButtonTooltipText = (): string => {
    if (!!report.ehrSync) return `This treatment plan has been sent to ${ehrName} and cannot be re-synced.`;
    else if (!report.learner?.externalId) return "The client does not have an external ID available.";
    else return "disabled";
  };

  // Monitor EHR sync status changes and show notifications
  useEffect(() => {
    const currentStatus = report?.ehrSync;
    const previousStatus = prevEhrSyncStatusRef.current;

    // Only show notification if status actually changed (not on initial render)
    if (previousStatus && currentStatus !== previousStatus) {
      if (currentStatus === ApiReportEhrSyncChoices.Completed) {
        notifySuccessPersistent(`EHR Sync completed successfully. Treatment plan synced to ${ehrName}.`);
      } else if (currentStatus === ApiReportEhrSyncChoices.Failure) {
        // Raw `error` from ehrSyncAlerts can include scraper exceptions /
        // upstream HTTP bodies / Python internals — keep it in Datadog for
        // support triage, but never render it to clinicians.
        const userMessage =
          report?.ehrSyncAlerts?.exportAlert?.userMessage || report?.ehrSyncAlerts?.importAlert?.userMessage;
        const errorMessage = report?.ehrSyncAlerts?.exportAlert?.error || report?.ehrSyncAlerts?.importAlert?.error;
        // Orgs that aren't cleared for EHR sync (ehr_sync_enabled off) only let frontera-internal
        // take EHR actions (enforce_ehr_sync_gate), so only the user-facing toast is gated for
        // everyone else; Datadog still records the failure.
        if (isEhrSyncCleared) {
          notifyErrorPersistent(
            userMessage || `We couldn't sync to ${ehrName}. Please try again. If the issue persists, contact support.`,
          );
        }
        datadogRum.addError(new Error("EHR Sync Failed"), {
          context: `EHR Sync Workflow Failure`,
          reportId: report.id,
          ehrName,
          errorMessage,
        });
      }

      // Org-level admin account: this sync polls report.ehrSync, not
      // ehr_connection, so refresh the connection when it resolves so a
      // credential failure surfaces on the top badge without a manual refresh.
      if (
        isOrgUsernamePassword &&
        (currentStatus === ApiReportEhrSyncChoices.Completed || currentStatus === ApiReportEhrSyncChoices.Failure)
      ) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    }

    // Update the ref for next comparison
    prevEhrSyncStatusRef.current = currentStatus;
  }, [
    report?.ehrSync,
    report?.id,
    report?.ehrSyncAlerts,
    ehrName,
    isOrgUsernamePassword,
    queryClient,
    isEhrSyncCleared,
  ]);

  const blockUntilGenerating = (navStepRoute: string, report: ReportNode): boolean => {
    const generatingRoutes = ["report-part-1", "report-part-2"];

    // Review must wait until every writeup section has finished generating —
    // otherwise unfinished fields reach the QA scanner and produce inaccurate results.
    if (navStepRoute === "review-and-sign") {
      return isGeneratingSection(getFilteredPageSectionsByLocation(report, "review-and-sign"));
    }

    return (
      generatingRoutes.includes(navStepRoute) &&
      report.reportSections.edges.filter((edge) => edge?.node?.pageLocation === navStepRoute).length === 0
    );
  };

  // log evaluation opening to datadog
  const base64DecodedId = atob(report.id);
  const databaseId = base64DecodedId.split("ReportType:")?.pop();
  datadogRum.addAction("report-opened", {
    reportId: databaseId,
    reportType: report.reportType,
    reportStatus: report.reportStatus,
  });

  const exportFramework = report.reportTemplate?.exportFramework;
  const isDocxTemplaterExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
  const isHandlebarsExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars;
  const isUnifiedExport = report.reportTemplate?.unifiedExport ?? false;
  // The template's `unifiedExport` field routes docxtemplater orgs through the handlebars S3 path.
  const useHandlebarsExportPath = isHandlebarsExport || (isDocxTemplaterExport && isUnifiedExport);
  const useDocxTemplaterExportPath = isDocxTemplaterExport && !isUnifiedExport;

  const handleReportExport = () => {
    if (useHandlebarsExportPath) {
      setIsExportModalOpen(true);
    } else {
      handleExport();
    }
  };

  const getExportButtonLabel = () => {
    if (isReportExporting) return "Exporting...";
    if (isPassageHealthUser) return "Export Report";
    return "Export";
  };

  // The Review step's Outline and Checkpoint slots, shown in the header until the report is completed. The review
  // controls follow them, so the header reads Outline, Checkpoint, Review.
  const reviewHeaderControls =
    currentRoute === "review-and-sign" && isReportQaScannerEnabled ? (
      <div className="flex gap-2 items-center">
        <ReviewViewSwitch />
      </div>
    ) : null;

  return (
    <Layout>
      <WizardStepTracker reportContext={reportContext} stepOrder={IA_ASSESSMENT_REPORTS_V2_STEP_ORDER} />
      <LayoutSubheader
        className={twMerge(
          "flex flex-col justify-center gap-y-2 bg-white py-3 px-4",
          // Skip the bottom border when the train-track progress nav renders
          // directly below — its own border-t would otherwise stack with ours.
          reportIsCompleted && "border-b border-border-medium",
        )}
      >
        <div className="flex w-full items-center justify-between px-2">
          <Paragraph colorType="primary" displayType="loud" className="flex min-w-0 flex-1 items-center gap-2">
            {/* The title gives way first, so the review buttons on the right are never pushed off screen. */}
            <span className="ph-no-capture truncate" title={headerTitle}>
              {headerTitle}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <StatusDefinitionTooltip status={headerStatus}>
                <StatusTag status={headerStatus} />
              </StatusDefinitionTooltip>
              {report.reportTemplate?.name && (
                <Badge
                  text={
                    <span className="truncate max-w-40" title={report.reportTemplate.name}>
                      {report.reportTemplate.name}
                    </span>
                  }
                  appearance="neutral"
                />
              )}
            </span>
          </Paragraph>
          <div className="ml-4 flex shrink-0 items-center gap-4">
            {!reportIsCompleted && reviewHeaderControls}
            <ReportReviewControls />
            {reportIsCompleted && (
              <div className="flex gap-2">
                <div className="flex gap-2">
                  {useDocxTemplaterExportPath ? (
                    <ExportWithTemplateV2
                      reportData={report}
                      refetchReport={async () => {
                        const result = await reportQuery.refetch();
                        return result.data?.getReports?.edges?.[0]?.node as ReportType | undefined;
                      }}
                    />
                  ) : (
                    <Button
                      text={
                        <span className="flex items-center gap-1.5">
                          <ArrowUpTrayIcon className="h-4 w-4" />
                          {getExportButtonLabel()}
                        </span>
                      }
                      disabled={isReportExporting}
                      appearance="text"
                      onClick={handleReportExport}
                    />
                  )}

                  {!isInSubmittedState &&
                    !isResolvingPayorStatus &&
                    !(
                      isPayorWithSubmissionIntegration &&
                      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Approved
                    ) && (
                      <Button
                        id="integrated-actions"
                        text="Edit"
                        appearance="secondary"
                        onClick={updateAssessmentStatus}
                        className="ring-green-400"
                      />
                    )}
                </div>

                {buttons.map(({ condition, text, onClick, disabled, key }) => {
                  if (!condition) return null;

                  // Already-synced / in-progress: explain why the sync button is
                  // disabled (passage/payor keep their own tooltips below).
                  const isSyncCompleted = report.ehrSync === ApiReportEhrSyncChoices.Completed;
                  const isSyncInProgress = ehrSyncPending || report.ehrSync === ApiReportEhrSyncChoices.Inprogress;
                  const isSyncAlreadyDone =
                    key === "sync-new-goals-to-ehr" &&
                    disabled &&
                    !isPassageHealthUser &&
                    !isPayorWithSubmissionIntegration &&
                    (isSyncCompleted || isSyncInProgress);
                  // Prior sync failed: button stays enabled for retry. The sticky
                  // subheader strip (EhrSyncFailureStrip) surfaces the failure
                  // message persistently — the button just needs a "Retry" label.
                  const isSyncFailed =
                    key === "sync-new-goals-to-ehr" &&
                    !isPassageHealthUser &&
                    !disabled &&
                    effectiveEhrSync === ApiReportEhrSyncChoices.Failure;
                  // Managed-onboarding gate: the sync button is disabled while the
                  // org isn't cleared; explain why on hover. The compact header
                  // can't host an inline notice, so a tooltip on the wrapper (which
                  // still receives hover even though the inner button is disabled)
                  // matches the passage/payor/already-synced pattern used here.
                  const isEhrSyncGated = key === "sync-new-goals-to-ehr" && !isEhrSyncCleared;
                  const isDisabledWithTooltip = disabled && (isPassageHealthUser || isPayorWithSubmissionIntegration);
                  const showTooltip = isDisabledWithTooltip || isSyncAlreadyDone || isEhrSyncGated;
                  let tooltipText: string;
                  if (isEhrSyncGated) {
                    tooltipText = `${EHR_SYNC_OUTBOUND_GATED_MESSAGE} Contact support to get started.`;
                  } else if (isSyncAlreadyDone) {
                    tooltipText = isSyncCompleted
                      ? `This treatment plan has already been synced to ${ehrName}.`
                      : `Sync to ${ehrName} is in progress.`;
                  } else if (isPayorWithSubmissionIntegration && key === "submit-to-payor") {
                    tooltipText = getPayorButtonTooltipText();
                  } else {
                    tooltipText = getPassageButtonTooltipText();
                  }
                  const button = (
                    <Button
                      key={text}
                      text={isSyncFailed ? `Retry Sync to ${ehrName}` : text}
                      disabled={disabled}
                      appearance="primary"
                      onClick={onClick}
                      className="ring-green-400"
                    />
                  );

                  return (
                    <div key={text} className="inline-block">
                      {showTooltip ? (
                        <Tooltip text={tooltipText} showInfoIcon={false}>
                          {button}
                        </Tooltip>
                      ) : (
                        button
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </LayoutSubheader>
      {/* Passage is an API-key org (org-level credentials, no per-provider reconnect flow),
          so the "reconnect your EHR" failure copy doesn't apply to them — excluded intentionally.
          When the org isn't cleared for EHR sync (ehr_sync_enabled off), only frontera-internal
          (always treated as cleared) can take EHR actions (enforce_ehr_sync_gate), so the failure
          is hidden for everyone else. */}
      {!isPassageHealthUser && isEhrSyncCleared && effectiveEhrSync === ApiReportEhrSyncChoices.Failure && (
        // z-[31] outranks the train-stop nav's LayoutSubheader (z-30) so the strip
        // stays visible when both are sticky at the same top offset (e.g. review-and-sign
        // before the report is marked Completed).
        <div className="sticky top-[calc(var(--nav-height,0px)+var(--subheader-height,0px))] z-[31]">
          <EhrSyncFailureStrip ehrSync={effectiveEhrSync} ehrSyncAlerts={report?.ehrSyncAlerts} />
        </div>
      )}
      {!reportIsCompleted && (
        <LayoutSubheader className="bg-background-secondary top-[calc(var(--nav-height,0px)+var(--subheader-height,0px))]">
          <TrainStopProgressNav
            steps={orderedRoutes.map((route, index) => {
              const blockRoute = blockUntilGenerating(route, report);
              return {
                index: index + 1,
                name: routeLabelMapping[route],
                status: statusFromCurrentRoute(currentRoute, route),
                to: route,
                navigationToEnabled: !blockRoute,
                Component: ({ children, className, to }) => (
                  <>
                    {blockRoute ? (
                      <span className={className}>{children}</span>
                    ) : (
                      <Link to={to} className={className} state={state}>
                        {children}
                      </Link>
                    )}
                  </>
                ),
              };
            })}
          />
        </LayoutSubheader>
      )}
      <ReviewerBanner />
      <Outlet />
      {/* Never show the per-provider login dialog to an org-admin account — they
          have no per-provider credentials to enter. Guards against a race where
          the auto-open effect runs before org metadata resolves and would
          otherwise strand the user in a login flow they cannot complete. */}
      {isEHRLogin && !isOrgUsernamePassword ? (
        <EHRLoginDialog
          open={isEHRLogin}
          setOpen={(open) => {
            setIsEHRLogin(open);
            if (!open) setPendingSyncAfterLogin(false);
          }}
          setSuccessLogin={setIsEHRLoginSuccess}
          setLoginOutcome={setEhrLoginOutcome}
        />
      ) : null}

      <EHROrgContactDialog
        open={isEhrOrgContactDialogOpen}
        setOpen={setIsEhrOrgContactDialogOpen}
        invalidCredentials={ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed}
      />

      <SyncConfirmDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        title={`Confirm Sync to ${ehrName}`}
        confirmAction={syncGoalsToEhr}
        disabled={ehrSyncPending}
        syncOrg={ehrName}
      />
      <ReportExportDialog
        open={isExportModalOpen}
        setOpen={setIsExportModalOpen}
        onExport={handleExport}
        selectedExportType={selectedExportType}
        setSelectedExportType={setSelectedExportType}
        isExporting={isReportExporting}
        report={report}
        onRetrySuccess={async () => {
          await reportQuery.refetch();
        }}
        isReportLoading={reportQuery.isFetching}
      />
      <PayorSubmissionDialog
        open={isPayorSubmissionDialogOpen}
        payorType={payorType}
        reportId={reportId ?? ""}
        clientName={clientName}
        dateOfBirth={
          report?.reportClientDetails?.birthDate ? formatDate(report.reportClientDetails.birthDate) : undefined
        }
        onSubmitToPayor={handleSubmitToPayor}
        onViewReport={() => {
          setPayorSubmissionFailed(false);
          setPayorSubmissionErrorMessage("");
          setIsPayorSubmissionDialogOpen(false);
        }}
        onClose={() => {
          setPayorSubmissionFailed(false);
          setPayorSubmissionErrorMessage("");
          setIsPayorSubmissionDialogOpen(false);
        }}
        isSubmitting={isSubmittingToPayorInProgress || isGeneratingPdfForSubmit}
        isSubmitDisabled={!isExportDataReady}
        submissionError={payorSubmissionFailed}
        submissionErrorMessage={payorSubmissionErrorMessage}
        isResubmission={payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision}
      />
      <PayorSubmittingDialog open={isPayorSubmittingDialogOpen} payorType={payorType} />
    </Layout>
  );
};

export const AssessmentReportV2Details: React.FC = () => {
  const { report } = useReport();

  if (!report) return <MissingReportPage />;

  return (
    <ReviewSessionProvider>
      <ReviewDataProvider>
        <SidebarViewProvider>
          <FinishReviewProvider>
            <AssessmentReportV2DetailsContent />
          </FinishReviewProvider>
        </SidebarViewProvider>
      </ReviewDataProvider>
    </ReviewSessionProvider>
  );
};
